// Easter egg unlock system
import { EASTER_EGG_CONFIGS, getEasterEggConfig } from '../config/easterEggs'
import { BASE_COLOR_THEMES } from '../config/colorThemes'

export interface EasterEgg {
  id: string
  name: string
  description: string
  unlocked: boolean
}

export interface UnlockedColorTheme {
  name: string
  primary: string
  secondary: string
  requiredEasterEgg?: string // ID of the easter egg required to unlock this color
  isLocked?: boolean
}

const EASTER_EGGS_STORAGE_KEY = 'about-me-easter-eggs'

// Export the centralized easter eggs for backward compatibility
export { EASTER_EGGS } from '../config/easterEggs'

// Get unlocked easter eggs from localStorage
export const getUnlockedEasterEggs = (): string[] => {
  try {
    const stored = localStorage.getItem(EASTER_EGGS_STORAGE_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

// Check if a specific easter egg is unlocked
export const isEasterEggUnlocked = (easterEggId: string): boolean => {
  const unlocked = getUnlockedEasterEggs()
  return unlocked.includes(easterEggId)
}

// Unlock an easter egg
export const unlockEasterEgg = (easterEggId: string): boolean => {
  const unlocked = getUnlockedEasterEggs()
  
  if (!unlocked.includes(easterEggId)) {
    unlocked.push(easterEggId)
    try {
      localStorage.setItem(EASTER_EGGS_STORAGE_KEY, JSON.stringify(unlocked))
      
      // Dispatch custom event for other components to listen to
      window.dispatchEvent(new CustomEvent('easterEggUnlocked', { 
        detail: { easterEggId }
      }))
      
      return true // Newly unlocked
    } catch {
      return false
    }
  }
  
  return false // Already unlocked
}

// Get all unlocked easter eggs with details
export const getUnlockedEasterEggDetails = (): EasterEgg[] => {
  const unlockedIds = getUnlockedEasterEggs()
  
  return EASTER_EGG_CONFIGS.map(config => ({
    id: config.id,
    name: config.name,
    description: config.description,
    unlocked: unlockedIds.includes(config.id)
  }))
}

// Reset all easter egg progress (for development/testing)
export const resetEasterEggProgress = (): void => {
  try {
    localStorage.removeItem(EASTER_EGGS_STORAGE_KEY)
    window.dispatchEvent(new CustomEvent('easterEggReset'))
    console.log('🔄 Easter egg progress reset')
  } catch {
    // Fail silently
  }
}

// Development helper to manually unlock an easter egg
export const devUnlockEasterEgg = (easterEggId: string): void => {
  const success = unlockEasterEgg(easterEggId)
  if (success) {
    console.log(`🎉 Dev unlock: ${easterEggId}`)
  } else {
    console.log(`ℹ️ Already unlocked: ${easterEggId}`)
  }
}

// Define color themes with unlock requirements
export const getColorThemesWithLocks = (): UnlockedColorTheme[] => {
  const baseThemes: UnlockedColorTheme[] = [
    ...BASE_COLOR_THEMES,
    // Add easter egg unlocked colors
    ...EASTER_EGG_CONFIGS.map(config => ({
      name: config.unlockedColor.name,
      primary: config.unlockedColor.primary,
      secondary: config.unlockedColor.secondary,
      requiredEasterEgg: config.id
    }))
  ]

  // Add lock status to each theme
  return baseThemes.map(theme => ({
    ...theme,
    isLocked: theme.requiredEasterEgg ? !isEasterEggUnlocked(theme.requiredEasterEgg) : false
  }))
}

// Get only unlocked color themes
export const getUnlockedColorThemes = (): UnlockedColorTheme[] => {
  return getColorThemesWithLocks().filter(theme => !theme.isLocked)
}

// Get easter egg description by ID
export const getEasterEggDescription = (easterEggId: string): string => {
  const easterEgg = getEasterEggConfig(easterEggId)
  return easterEgg ? easterEgg.description : 'Unknown easter egg'
}
